/** The thing you look at while you talk to the partner.
 *
 * The client's words: the screen "is very boring" while the student is
 * talking, and there should be "some circle with some design and flow like
 * structure, where the structure should have movement as the AI agent
 * speaks."
 *
 * So: a ring of layered waves around a core, drawn on a canvas. Each state
 * of the conversation moves differently, and that IS the information —
 * a learner should be able to tell at a glance whether the partner is
 * talking, whether it is their turn, or whether it is thinking:
 *
 *   speaking   large, fast, many overlapping waves — it is saying something
 *   listening  a slow, steady breath — your turn, take your time
 *   thinking   a tight, quick tremor — it has your sentence, wait
 *   idle       almost still
 *
 * Canvas rather than SVG paths: the shape is generated every frame from
 * offset sine waves, which is what makes it look organic rather than like a
 * looping GIF.
 */
import { useEffect, useRef } from "react";
import { useT } from "../../i18n";

export type OrbState = "idle" | "listening" | "thinking" | "speaking";

/** amplitude = how far the waves swing, speed = how fast, waves = how many
 *  layers overlap. Tuned so the four states are distinguishable at a glance
 *  rather than merely different numbers. */
const MOTION: Record<OrbState, { amplitude: number; speed: number; waves: number }> = {
  idle: { amplitude: 0.02, speed: 0.4, waves: 2 },
  listening: { amplitude: 0.06, speed: 0.8, waves: 3 },
  thinking: { amplitude: 0.05, speed: 2.6, waves: 4 },
  speaking: { amplitude: 0.16, speed: 1.9, waves: 5 },
};

const SIZE = 220;      // css pixels; the canvas is drawn at device scale

export function VoiceOrb({ state }: { state: OrbState }) {
  const t = useT();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Read through a ref inside the animation loop so a state change does not
  // restart it — the motion should morph, not jump.
  const stateRef = useRef<OrbState>(state);
  stateRef.current = state;

  useEffect(() => {
    const canvas = canvasRef.current;
    // jsdom (and any browser without 2d) gives no context. Nothing to draw,
    // and the label below still says what is happening.
    const ctx = canvas?.getContext?.("2d");
    if (!canvas || !ctx) return;

    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = SIZE * dpr;
    canvas.height = SIZE * dpr;
    ctx.scale(dpr, dpr);

    const mid = SIZE / 2;
    const base = SIZE * 0.3;
    let frame = 0;
    let raf = 0;

    const draw = (elapsed: number) => {
      const motion = MOTION[stateRef.current];
      ctx.clearRect(0, 0, SIZE, SIZE);

      // Layered rings, each a circle whose radius wobbles with its own
      // phase — where they overlap you get the "flow" the client asked for.
      for (let layer = 0; layer < motion.waves; layer += 1) {
        const phase = elapsed * motion.speed + layer * 1.7;
        const spread = 1 - layer / (motion.waves + 2);
        ctx.beginPath();
        for (let a = 0; a <= 360; a += 4) {
          const rad = (a * Math.PI) / 180;
          const wobble =
            Math.sin(rad * 3 + phase) * 0.6 + Math.sin(rad * 5 - phase * 0.7) * 0.4;
          const r = base * spread * (1 + wobble * motion.amplitude);
          const x = mid + Math.cos(rad) * r;
          const y = mid + Math.sin(rad) * r;
          if (a === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.closePath();
        // Reads on both themes: the brand hue at low alpha over whatever
        // ground the page is painted in.
        ctx.strokeStyle = `rgba(122, 154, 122, ${0.5 - layer * 0.07})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      // The core — a soft disc that swells slightly with the outer motion.
      const pulse = 1 + Math.sin(elapsed * motion.speed * 1.3) * motion.amplitude * 0.7;
      const core = base * 0.42 * pulse;
      const glow = ctx.createRadialGradient(mid, mid, 0, mid, mid, core);
      glow.addColorStop(0, "rgba(122, 154, 122, 0.85)");
      glow.addColorStop(1, "rgba(122, 154, 122, 0.15)");
      ctx.beginPath();
      ctx.arc(mid, mid, core, 0, Math.PI * 2);
      ctx.fillStyle = glow;
      ctx.fill();
    };

    if (reduced) {
      // One still frame: the shape is still informative, it just doesn't move.
      draw(0);
      return;
    }

    const loop = () => {
      frame += 1;
      draw(frame / 60);
      raf = window.requestAnimationFrame(loop);
    };
    raf = window.requestAnimationFrame(loop);
    return () => window.cancelAnimationFrame(raf);
  }, []);

  const label = t(
    state === "speaking"
      ? "orb.speaking"
      : state === "listening"
        ? "orb.listening"
        : state === "thinking"
          ? "orb.thinking"
          : "orb.idle",
  );

  return (
    <div className="flex flex-col items-center gap-2 py-2">
      <canvas
        ref={canvasRef}
        width={SIZE}
        height={SIZE}
        aria-hidden
        style={{ width: SIZE, height: SIZE }}
        data-orb-state={state}
      />
      <p role="status" className="text-sm font-semibold text-muted">
        {label}
      </p>
    </div>
  );
}
